import { RiCheckLine, RiFileCopyLine } from '@remixicon/react';
import type { ReactNode } from 'react';

import { useClipboard } from '@shared/hooks/clipboard';

import { Button } from '@playbook/atoms/button';
import { TextArea } from '@playbook/atoms/inputs/text-area';
import { TextInput } from '@playbook/atoms/inputs/text-input';

import css from './styles.module.css';

interface CopyableValueProps {
	name: string;
	value: string;

	label?: string;
	description?: string;
	help?: string;
}

/**
 *	A single-line value the reader is meant to copy — a key id, a URL.
 *
 *	A read-only text input rather than a code element, because Playbook's
 *	`TextInput` already carries a button inside the field, which is exactly the
 *	affordance this needs.
 */
export function CopyableValue({
	name,
	value,

	label,
	description,
	help,
}: CopyableValueProps): ReactNode {
	const { state, copy } = useClipboard();

	return (
		<TextInput
			readOnly
			name={name}
			value={value}
			label={label}
			description={description}
			help={help}
			inputButtonIcon={
				state === 'copied' ? <RiCheckLine /> : <RiFileCopyLine />
			}
			inputButtonLabel={state === 'copied' ? 'Copied' : 'Copy'}
			onInputButton={() => void copy(value)}
		/>
	);
}

interface CodeBlockProps {
	name: string;
	value: string;

	label?: string;
	description?: string;
	rows?: number;

	/** A second action beside Copy, such as downloading the value as a file. */
	action?: ReactNode;
}

/**
 *	A multi-line value — a PEM block, a shell command.
 *
 *	Playbook has no code block, and a read-only `TextArea` is the closest thing
 *	that exists: it keeps the monospace-shaped affordances a reader wants here
 *	(select-all, scroll) without inventing an entity. Copy sits beside it
 *	rather than inside, since a text area has no button slot.
 */
export function CodeBlock({
	name,
	value,

	label,
	description,
	rows = 6,

	action,
}: CodeBlockProps): ReactNode {
	const { state, copy } = useClipboard();

	return (
		<div className={css.block}>
			<div className={css.code}>
				<TextArea
					readOnly
					name={name}
					value={value}
					label={label}
					description={description}
					rows={rows}
				/>
			</div>
			<div className={css.actions}>
				{action}
				<Button
					label={state === 'copied' ? 'Copied' : 'Copy'}
					leadingIcon={
						state === 'copied' ? <RiCheckLine /> : <RiFileCopyLine />
					}
					onClick={() => void copy(value)}
				/>
			</div>
		</div>
	);
}
